import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./Button";
import type { TripSummaryWithPermission } from "@poi-planner/shared";
import { permissionToneConfig } from "../theme/colors";

interface TripCardProps {
  trip: TripSummaryWithPermission;
  onDelete: (id: string) => void;
  onShare?: (tripId: string) => void;
}

function formatDateRange(startDate: string, endDate: string): string {
  if (!startDate) return "No dates set";

  const start = new Date(startDate + "T00:00:00");
  const end = endDate ? new Date(endDate + "T00:00:00") : start;

  const startMonth = start.toLocaleDateString("en-US", { month: "short" });
  const endMonth = end.toLocaleDateString("en-US", { month: "short" });
  const startDay = start.getDate();
  const endDay = end.getDate();
  const year = start.getFullYear();

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}–${endDay}, ${year}`;
  }
  return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`;
}

export function TripCard({ trip, onDelete, onShare }: TripCardProps) {
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const permissionTone =
    trip.permission === "editor"
      ? permissionToneConfig.editor
      : permissionToneConfig.viewer;

  const handleClick = () => {
    navigate(`/trips/${trip.id}`);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showConfirm) {
      onDelete(trip.id);
    } else {
      setShowConfirm(true);
      setTimeout(() => setShowConfirm(false), 3000);
    }
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare?.(trip.id);
  };

  return (
    <div
      onClick={handleClick}
      data-testid="trip-card"
      data-trip-id={trip.id}
      className="group relative bg-white border border-border rounded-2xl p-5 cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-slate-200/50 hover:border-slate-300 hover:-translate-y-0.5"
    >
      {/* Permission Badge */}
      {!trip.isOwner && (
        <div
          className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold"
          style={{
            background: permissionTone.bg,
            color: permissionTone.color,
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "14px" }}
          >
            {trip.permission === "editor" ? "edit" : "visibility"}
          </span>
          <span>{trip.permission === "editor" ? "Editor" : "Viewer"}</span>
        </div>
      )}

      {/* Action buttons */}
      <div className="absolute top-3 right-3 flex items-center gap-1">
        {trip.isOwner && onShare && (
          <Button
            onClick={handleShare}
            variant="icon"
            icon="share"
            className="reveal-on-card-hover"
            title="Share trip"
            aria-label="Share trip"
          />
        )}
        {trip.isOwner && (
          <Button
            onClick={handleDelete}
            data-testid="delete-trip-button"
            variant={showConfirm ? "danger" : "icon"}
            icon={showConfirm ? "check" : "delete"}
            className={showConfirm ? "opacity-100" : "reveal-on-card-hover"}
            title={showConfirm ? "Click again to confirm" : "Delete trip"}
            aria-label={showConfirm ? "Confirm delete trip" : "Delete trip"}
          />
        )}
      </div>

      {/* Trip icon */}
      <div className="flex items-center justify-center size-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-200/50 mb-4">
        <span className="material-symbols-outlined text-2xl text-slate-500">
          explore
        </span>
      </div>

      {/* Trip name */}
      <h3 className="text-base font-bold text-primary mb-1 pr-8 line-clamp-2">
        {trip.name}
      </h3>

      {/* Date range */}
      <p className="text-sm text-secondary mb-3">
        {formatDateRange(trip.startDate, trip.endDate)}
      </p>

      {/* Footer with day count */}
      <div className="flex items-center gap-2 pt-3 border-t border-border">
        <span className="material-symbols-outlined text-base text-tertiary">
          calendar_today
        </span>
        <span className="text-xs font-semibold text-tertiary">
          {trip.dayCount} {trip.dayCount === 1 ? "day" : "days"}
        </span>
      </div>
    </div>
  );
}
